import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { TopNav, Footer } from '../components/PublicLayout'
import SEO from '../components/SEO'

// ─── Public Pricing page ─────────────────────────────────────────────────────
//
// Marketing pricing page for prospects (logged-out visitors).
//
// Logged-in users get redirected to /pricing/account, which is the existing
// plan picker (lets them switch plans, see their current tier, etc.). Keeps
// the marketing page free of conditional logic and the account UI free of
// marketing copy.
//
// The headline number is the monthly price. "About 10p a day" sits as a
// secondary line under the £3 figure — present but not gimmicky. The framing
// underneath ties the price to the cost of receipt chasing time, which is
// the actual value comparison bookkeepers will weigh.

const FEATURES_PER_CLIENT = [
  'Unlimited receipt requests per client',
  'Unlimited practice users',
  'Automatic chase emails',
  'Client portal & file uploads',
  'Lost receipt declarations',
  '7-year file retention',
  'CSV import from Xero, Sage, QuickBooks',
]

const FEATURES_PRO = [
  'Everything in the per-client plan',
  'Unlimited clients on a flat price',
  'Unlimited practice users',
  'Priority email support',
  'First access to new features',
]

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PublicPricing() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // If a logged-in user lands here (e.g. via footer link), send them to
  // their account pricing page where they can actually manage their plan.
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/pricing/account', { replace: true })
    }
  }, [user, authLoading, navigate])

  if (authLoading || user) {
    return <div className="loading-screen"><div className="spinner" /></div>
  }

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title="Pricing: £3 per client, or £25 flat for unlimited"
        description="Honest pricing for UK bookkeepers. £3 per client per month, or £25 flat for unlimited clients. About 10p a day per client. No setup fees, no per-seat charges, 30-day free trial."
        path="/pricing"
      />
      <TopNav />

      {/* Intro */}
      <section style={{ padding: '64px 0 32px' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 720, textAlign: 'center',
        }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 12,
          }}>
            Pricing
          </p>
          <h1 style={{
            marginBottom: 18, lineHeight: 1.15,
            fontSize: 'clamp(2rem, 5vw, 2.8rem)',
          }}>
            Honest pricing.<br/>No per-seat fees.
          </h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Two plans. Pick the one that fits your practice today, switch any
            time as you grow. Every plan includes the full feature set and
            unlimited practice users.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section style={{ padding: '16px 0 24px' }}>
        <div className="container" style={{ padding: '0 24px', maxWidth: 880 }}>
          <div style={{
            display: 'flex', gap: 24,
            justifyContent: 'center', flexWrap: 'wrap',
            alignItems: 'stretch',
          }}>
            <PlanCard
              title="Per client"
              price="£3"
              perUnit="per client, per month"
              tagline="About 10p a day per client"
              subtitle="Pay only for the clients you actually have on Receipt Chaser. Good for smaller practices or testing it on a few clients first."
              features={FEATURES_PER_CLIENT}
              recommended={false}
              cta="Start your free trial"
            />
            <PlanCard
              title="Pro"
              price="£25"
              perUnit="per month, flat"
              tagline="Unlimited clients on one price"
              subtitle="Best value once you're past 8 clients. Predictable monthly cost that doesn't grow as you take on more work."
              features={FEATURES_PRO}
              recommended={true}
              cta="Start your free trial"
            />
          </div>

          {/* Trust line */}
          <div style={{
            textAlign: 'center', marginTop: 28,
            fontSize: '0.82rem', color: 'var(--ink-faint)',
          }}>
            30-day free trial · No card required to start · Cancel any time · Secure payments via Stripe
          </div>
        </div>
      </section>

      {/* Value-vs-time framing */}
      <ValueFraming />

      {/* What's the catch */}
      <NoCatch />

      {/* FAQ */}
      <FAQ />

      {/* Final CTA */}
      <FinalCTA />

      <Footer />
    </div>
  )
}

// ─── Plan card ───────────────────────────────────────────────────────────────

function PlanCard({ title, price, perUnit, tagline, subtitle, features, recommended, cta }) {
  return (
    <div style={{
      flex: 1, minWidth: 280, maxWidth: 380,
      border: `2px solid ${recommended ? 'var(--accent)' : 'var(--surface-3)'}`,
      borderRadius: 'var(--radius-lg)',
      background: 'var(--white)',
      padding: 32,
      display: 'flex', flexDirection: 'column',
      position: 'relative',
      boxShadow: recommended ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
    }}>
      {recommended && (
        <div style={{
          position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--accent)', color: 'white',
          padding: '3px 16px', borderRadius: 100,
          fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em',
          textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>
          Most popular
        </div>
      )}

      <div style={{
        marginBottom: 10, fontSize: '0.82rem', fontWeight: 700,
        color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        {title}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '2.6rem', lineHeight: 1 }}>{price}</span>
        <span style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>{perUnit}</span>
      </div>

      <div style={{
        fontSize: '0.82rem', color: 'var(--accent)',
        fontWeight: 600, marginBottom: 14,
      }}>
        {tagline}
      </div>

      <div style={{
        fontSize: '0.88rem', color: 'var(--ink-muted)',
        marginBottom: 22, lineHeight: 1.55,
      }}>
        {subtitle}
      </div>

      <div style={{ flex: 1, display: 'grid', gap: 10, marginBottom: 24 }}>
        {features.map(f => (
          <div key={f} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            fontSize: '0.88rem', lineHeight: 1.5,
          }}>
            <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
            <span>{f}</span>
          </div>
        ))}
      </div>

      <Link
        to="/signup"
        className={`btn btn-lg ${recommended ? 'btn-primary' : 'btn-secondary'}`}
        style={{ width: '100%', justifyContent: 'center', textAlign: 'center' }}
      >
        {cta}
      </Link>
    </div>
  )
}

// ─── Sections ────────────────────────────────────────────────────────────────

// Value framing: ties price to the time bookkeepers spend chasing receipts.
// This is the actual value comparison they'll weigh, not the absolute price.
function ValueFraming() {
  return (
    <section style={{ padding: '56px 0 24px', background: 'var(--white)', marginTop: 48 }}>
      <div className="container" style={{
        padding: '0 24px', maxWidth: 760, textAlign: 'center',
      }}>
        <p style={{
          fontSize: '0.78rem', fontWeight: 600,
          color: 'var(--accent)', textTransform: 'uppercase',
          letterSpacing: '0.05em', marginBottom: 12,
        }}>
          What it's really costing you
        </p>
        <h2 style={{
          marginBottom: 18, lineHeight: 1.2,
          fontSize: 'clamp(1.6rem, 4vw, 2.1rem)',
        }}>
          How much is the back-and-forth costing you right now?
        </h2>
        <p style={{
          color: 'var(--ink-muted)', fontSize: '1rem',
          lineHeight: 1.65, marginBottom: 32,
        }}>
          Most bookkeepers spend 30 minutes to an hour a month chasing receipts
          for each active client. At a typical £30 an hour, that's £15 to £30
          per client, per month, in your own time. Receipt Chaser handles the
          chase for £3 a client. About 10p a day to stop sending the same
          "have you got that receipt yet" email.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 16, marginTop: 28,
        }}>
          <StatBlock big="10p" label="per day, per client on the per-client plan" />
          <StatBlock big="9" label="clients is the point where Pro becomes cheaper" />
          <StatBlock big="£25" label="flat per month on Pro, no matter how many clients" />
        </div>
      </div>
    </section>
  )
}

function StatBlock({ big, label }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--surface-2)',
      borderRadius: 'var(--radius)',
      padding: '20px 16px',
      textAlign: 'center',
    }}>
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: '1.8rem',
        color: 'var(--ink)', lineHeight: 1.1, marginBottom: 6,
      }}>
        {big}
      </div>
      <div style={{
        fontSize: '0.82rem', color: 'var(--ink-muted)', lineHeight: 1.5,
      }}>
        {label}
      </div>
    </div>
  )
}

// Anticipates "what's the catch" objections common to SaaS pricing pages.
function NoCatch() {
  const items = [
    {
      heading: 'No setup fees',
      body: 'Sign up, import your clients, start sending requests in the same hour.',
    },
    {
      heading: 'No per-seat charges',
      body: 'Add as many people from your practice as you need. The price stays the same.',
    },
    {
      heading: 'No long contract',
      body: 'Pay monthly. Cancel from your account settings whenever you want.',
    },
  ]
  return (
    <section style={{ padding: '56px 0' }}>
      <div className="container" style={{ padding: '0 24px', maxWidth: 880 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h2 style={{
            marginBottom: 10, lineHeight: 1.2,
            fontSize: 'clamp(1.5rem, 4vw, 1.9rem)',
          }}>
            What's not in the small print.
          </h2>
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.98rem' }}>
            Things some other tools charge for. Things we don't.
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 20,
        }}>
          {items.map(item => (
            <div key={item.heading} style={{
              background: 'var(--white)',
              border: '1px solid var(--surface-2)',
              borderRadius: 'var(--radius)',
              padding: 22,
            }}>
              <h3 style={{
                fontSize: '1rem', fontWeight: 600,
                color: 'var(--ink)', marginBottom: 8, lineHeight: 1.3,
              }}>
                {item.heading}
              </h3>
              <p style={{
                fontSize: '0.88rem', color: 'var(--ink-muted)',
                lineHeight: 1.6, margin: 0,
              }}>
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// FAQ: short, focused on actual pricing/billing questions a prospect would
// ask before paying. Not a general product FAQ.
function FAQ() {
  const qs = [
    {
      q: 'What happens after the 30-day free trial?',
      a: "You'll be asked to pick a plan. Nothing is charged automatically. If you don't pick one, your account pauses and your data stays safe for 90 days while you decide.",
    },
    {
      q: 'How does the per-client plan count clients?',
      a: 'Active clients on Receipt Chaser. You only pay for clients you have actively set up in the system. Archive a client and the count drops on your next billing date.',
    },
    {
      q: 'When should I switch to the Pro plan?',
      a: "Pro is £25 a month for unlimited clients, which works out cheaper than the per-client plan once you're past 8 clients. You can switch any time from your settings.",
    },
    {
      q: 'Are there any per-user charges?',
      a: 'No. Add as many people from your practice as you need at no extra cost. Bookkeeper, junior, admin, accountant on the side. All included.',
    },
    {
      q: 'How does billing work?',
      a: 'Monthly, in advance, via Stripe. You can update your card or cancel at any time from your account settings.',
    },
    {
      q: 'What if I have a question that\'s not here?',
      a: (
        <>
          Drop us a line on the{' '}
          <Link to="/contact" style={{ color: 'var(--accent)', fontWeight: 500 }}>contact page</Link>
          {' '}and we'll get back to you shortly.
        </>
      ),
    },
  ]
  return (
    <section style={{ padding: '40px 0 64px', background: 'var(--white)' }}>
      <div className="container" style={{ padding: '0 24px', maxWidth: 760 }}>
        <h2 style={{
          textAlign: 'center', marginBottom: 32,
          lineHeight: 1.2, fontSize: 'clamp(1.5rem, 4vw, 1.9rem)',
        }}>
          Pricing questions.
        </h2>
        <div style={{ display: 'grid', gap: 20 }}>
          {qs.map(({ q, a }) => (
            <div key={q} style={{
              borderBottom: '1px solid var(--surface-2)',
              paddingBottom: 18,
            }}>
              <h3 style={{
                fontSize: '0.98rem', fontWeight: 600,
                color: 'var(--ink)', marginBottom: 8, lineHeight: 1.4,
              }}>
                {q}
              </h3>
              <p style={{
                fontSize: '0.92rem', color: 'var(--ink-muted)',
                lineHeight: 1.65, margin: 0,
              }}>
                {a}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FinalCTA() {
  return (
    <section style={{ padding: '64px 24px', background: 'var(--ink)' }}>
      <div className="container" style={{
        maxWidth: 640, textAlign: 'center',
      }}>
        <h2 style={{
          color: 'white', marginBottom: 14, lineHeight: 1.2,
          fontSize: 'clamp(1.6rem, 4vw, 2.1rem)',
        }}>
          Try it free for 30 days.
        </h2>
        <p style={{
          color: 'rgba(255,255,255,0.7)', fontSize: '1rem',
          marginBottom: 28, lineHeight: 1.55,
        }}>
          No card needed. Set it up in an hour, send your first request the
          same day, see whether it actually fixes the chase.
        </p>
        <Link
          to="/signup"
          className="btn btn-primary btn-lg"
          style={{ padding: '14px 32px' }}
        >
          Start your free trial →
        </Link>
      </div>
    </section>
  )
}
